import {
  Alert,
  Button,
  message,
  Modal,
  Segmented,
  Select,
  Tag,
  TimePicker,
} from "antd";
import {
  ClockCircleOutlined,
  HourglassOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import ClockFaceTimePicker from "../ClockFaceTimePicker";
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../../../../authentication/authHelpers";
import dayjsTZ from "../../../../../utils/dayjs";
import { RichTextEditor } from "../../../../commonComponents";
import {
  getDisabledBeforeCustomHours,
  getDisabledBeforeCustomMinutes,
} from "../../../utils/disabledTimeFilters";
import { useDispatch, useSelector } from "react-redux";
import {
  endDoubleShift,
  endShift,
  getLastOnGoingShift,
} from "../../../helpers/apiCall";
import swal from "sweetalert";
import axios from "axios";
import { publish } from "../../../../../utils/socket/dashboardSocket";
import { editClocking } from "../../../../../store/actions/addClockings";
import { eventTracker } from "../../../../../utils/eventTracker";
import useReactIpLocation from "react-ip-details";
import {
  fetchScheduledShift,
  resolvePlannedShift,
} from "../../../../../utils/scheduling/findScheduledShift";
import {
  canBypassGrace,
  evaluateGracePeriod,
  getGracePeriodMinutes,
  graceInfoMessage,
  MIN_NOTE_CHARS,
  noteCharCount as countNoteChars,
  noteMeetsMinLength,
} from "../gracePeriod";
import {
  evaluatePlannedClockOutWindow,
  getEditTimeCeiling,
  isAboveEditCeiling,
  snapRollDownToPlannedEnd,
  lateClockOutNoteText,
} from "../plannedWindow";
import {
  classifyOpenShiftClose,
  overnightBannerCopy,
} from "../overnightClose";
import {
  isValidSameDayEnd,
  sameDayDisabledHours,
  sameDayDisabledMinutes,
  sameDayEndFromHm,
  suggestSameDayEnd,
} from "../forgot/forgotSameDayEnd";
import { resolveClockingTimezone } from "../../../../../utils/timezone/teamTimezone";
import "../shiftModal.scss";

const PICKER_MODE_KEY = "flexhr.pickerMode";
const readPickerMode = () => {
  try {
    const v = window.localStorage.getItem(PICKER_MODE_KEY);
    return v === "clock" ? "clock" : "classic";
  } catch (_) {
    return "classic";
  }
};

const { Option } = Select;

const formatHm = (totalMinutes) => {
  const m = Math.max(0, Math.round(totalMinutes || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

function ClockOutModal({
  clockOutVisible,
  setClockOutVisible,
  onSave,
  lastShift,
  apiData,
  setLoading,
  setLoadingColor,
  loadShiftData,
  setBarStart,
  resetValues,
  setNewShift,
  setLastShift,
  shiftConfiguration,
}) {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const { programFields } = useSelector((state) => state.programFields);
  const auth = useAuth();
  const teamTimezone = resolveClockingTimezone(
    lastShift,
    auth?.employee?.employeeTeam,
    programFields
  );
  const [newShift, setNewSh] = useState({ note: "" });
  const [graceWarnings, setGraceWarning] = useState({
    enable: false,
    text: "",
  });
  /** null = unset · false = same calendar day · true = after midnight */
  const [forgotKind, setForgotKind] = useState(null);
  const [gracePeriod, setGracePeriod] = useState(0);

  const isForgotShift = useMemo(
    () =>
      classifyOpenShiftClose(lastShift, undefined, teamTimezone) === "forgot",
    [lastShift, teamTimezone]
  );
  // Scheduled-shift override (see NewUserShift.jsx for rationale).
  const [scheduledShift, setScheduledShift] = useState(undefined);
  const [editedTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerMode, setPickerMode] = useState(readPickerMode);
  useEffect(() => {
    try {
      window.localStorage.setItem(PICKER_MODE_KEY, pickerMode);
    } catch (_) {}
  }, [pickerMode]);
  const dispatch = useDispatch();
  const { ipResponse } = useReactIpLocation({ numberToConvert: 100 });

  //region ip
  const ip = useMemo(async () => {
    const res = await axios.get("https://api.ipify.org/?format=json");

    if (!!res?.data?.ip) return res?.data?.ip;
    else return "";
  }, []);

  // Resolve today's rotation-system assignment when the clock-out modal
  // opens. Degrades to `null` on any error so the legacy fallback in
  // `plannedShift`/`getAutoSuggest` kicks in unchanged.
  useEffect(() => {
    if (!clockOutVisible) return;
    const employeeId =
      lastShift?.employeeId || auth?.employeeId || apiData?.[0]?.employeeId;
    if (!employeeId) return;
    let cancelled = false;
    (async () => {
      const res = await fetchScheduledShift(
        employeeId,
        Date.now(),
        teamTimezone
      );
      if (!cancelled) setScheduledShift(res || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [clockOutVisible, lastShift?.employeeId, auth?.employeeId, apiData]);

  // Snapshot-on-open: freeze wall-clock "now" for same-day closes.
  // Forgotten shifts stay empty until the user picks same-day / after-midnight.
  useEffect(() => {
    if (!clockOutVisible) {
      setNewSh((prev) =>
        prev?.clockOutDate || prev?.note
          ? { note: "", clockOutDate: null }
          : prev
      );
      setForgotKind(null);
      return;
    }
    const forgot =
      classifyOpenShiftClose(lastShift, undefined, teamTimezone) === "forgot";
    if (forgot) {
      setNewSh((prev) => ({
        ...prev,
        note: prev?.note || "",
        clockOutDate: prev?.clockOutDate || null,
      }));
      return;
    }
    setNewSh((prev) => ({
      ...prev,
      clockOutDate: prev?.clockOutDate || getAutoSuggest(dayjsTZ()),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockOutVisible, lastShift?.clockingId, teamTimezone]);

  // Seed end time when forgotten-shift kind is chosen.
  useEffect(() => {
    if (!clockOutVisible || !isForgotShift || forgotKind === null) return;
    if (forgotKind === true) {
      setNewSh((prev) => ({
        ...prev,
        clockOutDate: dayjsTZ().second(0).millisecond(0),
      }));
      setGraceWarning({
        enable: true,
        text: "Shënimi me arsye është i detyrueshëm për turnin e harruar.",
      });
      return;
    }
    const suggested = suggestSameDayEnd(lastShift?.clockInDate);
    setNewSh((prev) => ({
      ...prev,
      clockOutDate: suggested || null,
    }));
    setGraceWarning({
      enable: true,
      text: "Shënimi me arsye është i detyrueshëm për turnin e harruar.",
    });
  }, [
    forgotKind,
    clockOutVisible,
    isForgotShift,
    lastShift?.clockInDate,
  ]);

  const graceBypass = canBypassGrace(auth?.userRole);

  //region AUTO SUGGEST
  // Roll-down only: if live time is after planned end (within +15m),
  // snap to end (e.g. 21:45 → 21:30). Earlier picks are kept so the
  // employee can roll further down without being snapped back up.
  const getAutoSuggest = (time) => {
    const rolled = snapRollDownToPlannedEnd(
      time,
      plannedShift?.end,
      undefined,
      teamTimezone
    );
    if (rolled) return rolled;

    if (plannedShift?.end) {
      return dayjsTZ(time);
    }

    const t = dayjsTZ(time).valueOf();
    const eveningSnap = dayjsTZ()
      .set("hour", 21)
      .set("minute", 30)
      .set("second", 0);
    const morningSnap = dayjsTZ()
      .set("hour", 18)
      .set("minute", 30)
      .set("second", 0);

    // Legacy: roll down only when after the snap anchor.
    if (
      t > morningSnap.valueOf() &&
      t <
        dayjsTZ()
          .set("hour", 18)
          .set("minute", 32)
          .set("second", 30)
          .valueOf()
    ) {
      return morningSnap;
    }
    if (
      t > eveningSnap.valueOf() &&
      t <
        dayjsTZ()
          .set("hour", 21)
          .set("minute", 45)
          .set("second", 30)
          .valueOf()
    ) {
      return eveningSnap;
    }

    return dayjsTZ(time);
  };

  //region ONCHANGE
  const onChange = (e) => {
    if (!e) return;
    const time = dayjsTZ(e)
      .set("hour", e.get("hour"))
      .set("minute", e.get("minute"))
      .set("second", 0);

    // Forgotten shift: reconstruct past end time (no grace / future ceiling).
    if (isForgotShift) {
      if (forgotKind === null) {
        message.info({
          key: "clockout",
          content: "Zgjidhni fillimisht llojin e mbylljes së turnit.",
        });
        return;
      }
      if (forgotKind === false) {
        if (!isValidSameDayEnd(lastShift?.clockInDate, time)) {
          message.error({
            key: "clockout",
            content:
              "Ora e mbarimit duhet të jetë pas fillimit dhe para mesnatës së së njëjtës ditë.",
          });
          return;
        }
        setNewSh((prev) => ({
          ...prev,
          clockOutDate: sameDayEndFromHm(lastShift?.clockInDate, time),
        }));
      } else {
        setNewSh((prev) => ({ ...prev, clockOutDate: time }));
      }
      setGraceWarning({
        enable: true,
        text: "Shënimi me arsye është i detyrueshëm për turnin e harruar.",
      });
      return;
    }

    const suggested = getAutoSuggest(dayjsTZ());
    const ceiling = getEditTimeCeiling(dayjsTZ(), suggested, {
      bypass: graceBypass,
      timezone: teamTimezone,
    });

    if (isAboveEditCeiling(time, ceiling, teamTimezone)) {
      message.error({
        key: "clockout",
        content: "Nuk mund të zgjedhësh një kohë në të ardhmen!",
      });
      setNewSh((prev) => ({
        ...prev,
        clockOutDate: suggested,
      }));
      setGraceWarning({
        enable: false,
        text: "",
      });
      return;
    }

    if (!(gracePeriod > 0 || graceBypass)) {
      message.info({
        key: "clockout",
        content: "Juve nuk ju lejohet të ndryshoni kohën e mbarimit!",
      });
      setNewSh((prev) => ({
        ...prev,
        clockOutDate: suggested,
      }));
      return;
    }

    const windowCheck = evaluatePlannedClockOutWindow(
      plannedShift,
      time,
      lastShift?.clockInDate,
      { timezone: teamTimezone }
    );
    // Only hard-fail invalid range (out before in). Off-schedule → note.
    if (!windowCheck.ok) {
      message.error({ key: "clockout", content: windowCheck.reason });
      return;
    }
    if (checkGracePeriod(time)) {
      if (windowCheck.noteRequired) {
        setGraceWarning({
          text: windowCheck.reason,
          enable: true,
        });
      }
      // Keep the employee's pick — do not re-snap upward to planned end.
      setNewSh((prev) => ({
        ...prev,
        clockOutDate: time,
      }));
    } else {
      message.error({
        key: "clockout",
        content:
          "Aktualisht, nuk lejohet ndryshimi i orës së daljes. Kontaktoni me supervizorin përkatës!",
      });
    }
  };

  //region GRACE PERIOD
  useEffect(() => {
    if (!clockOutVisible) return;
    setGracePeriod(getGracePeriodMinutes(programFields));
  }, [clockOutVisible, programFields]);

  const checkGracePeriod = (date) => {
    const result = evaluateGracePeriod(date, gracePeriod, {
      bypass: graceBypass,
    });
    setGraceWarning({
      text: result.noteRequired ? result.text : "",
      enable: result.noteRequired,
    });
    return result.allowed;
  };

  //region CLOCKOUT SEND
  const onClockOutSend = async () => {
    // Forgotten open shift — same modal/picker, dedicated close rules.
    if (isForgotShift) {
      if (forgotKind === null) {
        message.error({
          key: "clockout",
          content: "Zgjidhni nëse mbarimi ishte të njëjtën ditë apo pas mesnatës.",
        });
        return;
      }
      if (!newShift?.clockOutDate) {
        message.error({
          key: "clockout",
          content: "Zgjidhni orën e mbarimit të turnit.",
        });
        return;
      }
      if (!noteMeetsMinLength(newShift?.note)) {
        message.error({
          key: "clockout",
          content: `Shënimi me arsye është i detyrueshëm (min. ${MIN_NOTE_CHARS} karaktere).`,
        });
        setGraceWarning({
          enable: true,
          text: "Shënimi me arsye është i detyrueshëm për turnin e harruar.",
        });
        return;
      }
      if (
        forgotKind === false &&
        !isValidSameDayEnd(lastShift?.clockInDate, newShift.clockOutDate)
      ) {
        message.error({
          key: "clockout",
          content:
            "Ora e mbarimit duhet të jetë pas fillimit dhe para mesnatës së së njëjtës ditë.",
        });
        return;
      }

      message.loading({
        key: "clockout",
        content: "Duke ruajtur të dhënat...",
      });
      setLoadingColor("#ea3943");
      setLoading(true);
      try {
        const plainNote = String(newShift.note || "")
          .replace(/<[^>]+>/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        await onClockForgot({
          fillimi: dayjsTZ(lastShift?.clockInDate),
          mbarimi: newShift.clockOutDate,
          shenimi: plainNote,
          isAfterMidnight: forgotKind,
        });
        message.destroy("clockout");
        setClockOutVisible(false);
      } catch (err) {
        console.error(err);
        message.error({
          key: "clockout",
          content: "Mbyllja e turnit dështoi. Provoni përsëri.",
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    const outCandidate = dayjsTZ(newShift?.clockOutDate || dayjsTZ())
      .second(0)
      .millisecond(0);
    const windowCheck = evaluatePlannedClockOutWindow(
      plannedShift,
      outCandidate,
      lastShift?.clockInDate,
      { timezone: teamTimezone }
    );
    if (!windowCheck.ok) {
      message.error({ key: "clockout", content: windowCheck.reason });
      return;
    }

    const graceResult = evaluateGracePeriod(outCandidate, gracePeriod, {
      bypass: graceBypass,
    });
    const noteRequiredNow =
      windowCheck.noteRequired ||
      graceResult.noteRequired ||
      graceWarnings.enable;
    if (noteRequiredNow) {
      setGraceWarning({
        text:
          windowCheck.reason ||
          graceResult.text ||
          graceWarnings.text ||
          lateClockOutNoteText(),
        enable: true,
      });
    }
    if (noteRequiredNow && !noteMeetsMinLength(newShift?.note)) {
      message.error({
        key: "clockout",
        content: `Shënimi me arsye është i detyrueshëm (min. ${MIN_NOTE_CHARS} karaktere).`,
      });
      return;
    }

    if (
      outCandidate.diff(dayjsTZ(lastShift?.clockInDate), "minutes", true) <= 2
    ) {
      message.info({
        key: "clockout",
        content: "Ju nuk mund të mbyllni një turn më të vogël se 2 minuta!",
      });
      return;
    }

    // When grace does not allow the edited past time, snap suggest — but
    // always pass the concrete out time into onClockOut (no stale state).
    const outToPost = graceResult.allowed
      ? outCandidate
      : getAutoSuggest(dayjsTZ());

    setNewSh((prev) => ({
      ...prev,
      clockOutDate: outToPost,
    }));

    eventTracker("Clock Out", "click", "Ended shift");
    message.loading({
      key: "clockout",
      content: "Duke ruajtur të dhënat...",
    });
    setLoadingColor("#ea3943");
    setLoading(true);
    try {
      await onClockOut({
        outDate: outToPost,
        note: newShift?.note || null,
      });
      message.destroy("clockout");
    } catch (err) {
      console.error(err);
      message.error({
        key: "clockout",
        content: "Mbyllja e turnit dështoi. Provoni përsëri.",
      });
    } finally {
      setLoading(false);
    }
  };

  //region ON CLOCKOUT
  const onClockOut = async ({ outDate, note } = {}) => {
    const closeKind = classifyOpenShiftClose(lastShift, undefined, teamTimezone);
    const resolvedOut = outDate ?? newShift?.clockOutDate ?? dayjsTZ();
    const resolvedNote = note !== undefined ? note : newShift?.note;

    const buildAndSend = async (type) => {
      let clockOut;
      if (editedTime !== null) {
        if (!noteMeetsMinLength(resolvedNote) && !resolvedNote) {
          message.error({
            key: "clockout",
            content: "Ju duhet të vendosni një shënim.",
          });
          return;
        }
        clockOut = {
          category: "manual",
          employeeId: lastShift.employeeId,
          timezone: teamTimezone,
          clockOutNote: resolvedNote || null,
          editedClockOut: true,
          finishedAt: dayjsTZ().valueOf(),
          approved: false,
        };
        await handleClockOut(clockOut, resolvedOut, type);
      } else {
        clockOut = {
          category: "automatic",
          editedClockOut: false,
          employeeId: lastShift.employeeId,
          timezone: teamTimezone,
          clockOutNote: resolvedNote || null,
          finishedAt: dayjsTZ().valueOf(),
          approved: false,
        };
        await handleClockOut(clockOut, resolvedOut, type);
      }
    };

    if (closeKind === "same_day") {
      await buildAndSend("single");
    } else if (closeKind === "forgot") {
      // Forgotten shifts are closed via onClockOutSend → onClockForgot
      // (same shiftc modal). Do not open the legacy Forgot dialog.
      throw new Error("forgot_shift_use_forgot_flow");
    } else {
      // closeKind "none" — still try to close if we have an open lastShift
      if (
        lastShift?.clockingId &&
        (lastShift.clockOutDate == null || lastShift.clockOutDate === "")
      ) {
        await buildAndSend("single");
      } else {
        throw new Error("no_open_shift_to_close");
      }
    }
  };

  //region >handleClockOut()
  const handleClockOut = async (shift, val, type) => {
    if (!shift?.employeeId) {
      throw new Error("missing_employee");
    }
    // Prefer the shift this modal was opened for — re-scanning apiData can
    // pick a different open row when multiple unfinished clockings exist.
    let activeShift =
      (lastShift?.clockingId &&
        (lastShift.clockOutDate == null || lastShift.clockOutDate === "") &&
        lastShift) ||
      getLastOnGoingShift(apiData);
    if (!activeShift?.clockingId) {
      message.error("Nuk u gjet turni i hapur për mbyllje.");
      throw new Error("no_open_shift");
    }
    activeShift = {
      ...activeShift,
      clockingLogs: [
        ...(Array.isArray(activeShift.clockingLogs)
          ? activeShift.clockingLogs
          : []),
        {
          createdAt: dayjsTZ().valueOf(),
          note: "Krijuar nga vetë punonjësi.",
          newShiftByAdmin: false,
          newShiftByUser: true,
          username: auth?.user?.given_name + " " + auth?.user?.family_name,
          userSub: auth?.user?.userSub,
          ipResponse: {
            ...ipResponse,
            IPv4: typeof ip === "string" ? ip : "",
          },
        },
      ],
    };

    const outMs = dayjsTZ(val).valueOf();
    setSaving(true);
    try {
      if (type === "single") {
        await endShift(activeShift, shift, val);
      } else {
        await endDoubleShift(activeShift, shift, val);
      }
    } catch (err) {
      setSaving(false);
      throw err;
    }

    await loadShiftData(activeShift.employeeId);
    setNewSh({ note: "" });
    setNewShift(false);
    setSaving(false);
    setLastShift({
      ...activeShift,
      clockOutDate: outMs,
    });

    swal({
      title: `Turni u përfundua me sukses në orën ${dayjsTZ(val).format(
        "HH:mm"
      )}!`,
      icon: "success",
      className: isDarkMode ? "custum-swal-dark" : "custum-swal",
    }).then(() => {
      setBarStart(true);
    });

    dispatch(
      editClocking({
        ...activeShift,
        clockOutDate: outMs,
      })
    );
    resetValues();
    setLoading(false);
    setClockOutVisible(false);
    // Admin OS toast comes from AuthenticatedRoute on user_clockOut_received
    // (same mapping as clock-in — avoids a second notification WS fan-out).
    publish({
      request: "user_clockOut",
      body: {
        ...activeShift,
        clockOutDate: outMs,
        username:
          `${auth?.userAccess?.[0]?.given_name || ""} ${
            auth?.userAccess?.[0]?.family_name || ""
          }`.trim() || activeShift?.username,
      },
      id: activeShift.employeeId,
    });
  };

  //region CLOCK FORGOR
  const onClockForgot = async (obj) => {
    const { isAfterMidnight } = obj;
    let clockForgot = {
      employeeId: lastShift.employeeId,
      editedClockOut: !isAfterMidnight,
      forgotClockOut: !isAfterMidnight,
      clockOutNote: obj.shenimi,
      finishedAt: dayjsTZ().valueOf(),
      approved: false,
      timezone: teamTimezone,
    };
    const time = dayjsTZ(obj.mbarimi);

    if (isAfterMidnight) {
      eventTracker("Clock Out", "click", "Ended midnight shift");
      await handleClockOut(clockForgot, time, "double");
    } else {
      eventTracker("Clock Out", "click", "Ended forgotten shift");
      // Stamp picked H:M onto the clock-in calendar day (not "today").
      const stamped =
        sameDayEndFromHm(lastShift?.clockInDate, obj.mbarimi) || time;
      await handleClockOut(clockForgot, stamped, "single");
    }
  };

  //region DERIVED UI VALUES
  const userFirst =
    auth?.userAccess?.[0]?.given_name || auth?.user?.given_name || "";
  const userLast =
    auth?.userAccess?.[0]?.family_name || auth?.user?.family_name || "";
  const userFullName = `${userFirst} ${userLast}`.trim();

  // Rotation for the day wins; else static link to 10:00–18:30 / 13:30–21:30.
  const plannedShift = useMemo(() => {
    const applied = Array.isArray(shiftConfiguration?.appliedShifts)
      ? shiftConfiguration.appliedShifts.filter(Boolean)
      : [];
    const catalogue = Array.isArray(shiftConfiguration?.applicableShifts)
      ? shiftConfiguration.applicableShifts.filter(Boolean)
      : [];
    return resolvePlannedShift(scheduledShift, applied, {
      at: dayjsTZ(),
      timezone: teamTimezone,
      catalogueShifts: catalogue,
    });
  }, [scheduledShift, shiftConfiguration, teamTimezone]);

  // Soft off-schedule / early-leave warning while the modal is open.
  useEffect(() => {
    if (!clockOutVisible) return;
    const outAt = newShift?.clockOutDate || dayjsTZ();
    const scheduleCheck = evaluatePlannedClockOutWindow(
      plannedShift,
      outAt,
      lastShift?.clockInDate,
      { timezone: teamTimezone }
    );
    if (scheduleCheck.noteRequired) {
      setGraceWarning({
        text: scheduleCheck.reason,
        enable: true,
      });
    }
  }, [
    clockOutVisible,
    plannedShift?.start,
    plannedShift?.end,
    plannedShift?.label,
    newShift?.clockOutDate,
    lastShift?.clockInDate,
    teamTimezone,
  ]);

  // Effective clock-out for visual previews. Read the stored value as-is
  // (already seeded via getAutoSuggest on open / "Tani"). Re-snapping here
  // would force 21:40 back to 21:30 and block "set forward to actual time".
  const previewClockOut = useMemo(() => {
    if (newShift?.clockOutDate) {
      return dayjsTZ(newShift.clockOutDate);
    }
    return null;
  }, [newShift?.clockOutDate]);

  const elapsedMinutes = useMemo(() => {
    if (!lastShift?.clockInDate || !previewClockOut) return 0;
    const diff = dayjsTZ(previewClockOut).diff(
      dayjsTZ(lastShift.clockInDate),
      "minute"
    );
    return diff > 0 ? diff : 0;
  }, [previewClockOut, lastShift?.clockInDate]);

  const progressPct = useMemo(() => {
    if (!plannedShift?.minutes) {
      return Math.min(100, (elapsedMinutes / 480) * 100);
    }
    return Math.min(100, (elapsedMinutes / plannedShift.minutes) * 100);
  }, [elapsedMinutes, plannedShift]);

  const deltaMinutes = plannedShift
    ? elapsedMinutes - plannedShift.minutes
    : null;

  const noteCharCount = useMemo(
    () => countNoteChars(newShift?.note),
    [newShift?.note]
  );

  const noteRequired = graceWarnings.enable || isForgotShift;
  const shiftWindowSuspicious =
    !!plannedShift && plannedShift.minutes <= 0 && !isForgotShift;

  const overnightBanner = useMemo(
    () => overnightBannerCopy(lastShift, undefined, teamTimezone),
    [lastShift, teamTimezone]
  );

  // Disabled-hours for the clock-face picker.
  const clockOutDisabledHours = useMemo(() => {
    if (isForgotShift && forgotKind === false) {
      return sameDayDisabledHours(lastShift?.clockInDate);
    }
    if (isForgotShift && forgotKind === true) {
      return [];
    }
    const arr = [];
    if (lastShift?.clockInDate) {
      const inH = dayjsTZ(lastShift.clockInDate).hour();
      for (let h = 0; h < inH; h += 1) arr.push(h);
    }
    const referenceH = newShift?.clockOutDate
      ? dayjsTZ(newShift.clockOutDate).hour()
      : dayjsTZ().hour();
    for (let h = referenceH + 1; h <= 23; h += 1) {
      if (!arr.includes(h)) arr.push(h);
    }
    return arr;
  }, [
    lastShift?.clockInDate,
    newShift?.clockOutDate,
    isForgotShift,
    forgotKind,
  ]);

  const classicDisabledTime = () => {
    if (isForgotShift && forgotKind === false) {
      return {
        disabledHours: () => sameDayDisabledHours(lastShift?.clockInDate),
        disabledMinutes: (hour) =>
          sameDayDisabledMinutes(lastShift?.clockInDate, hour),
      };
    }
    if (isForgotShift) {
      return { disabledHours: () => [], disabledMinutes: () => [] };
    }
    return {
      disabledHours:
        lastShift?.clockInDate != null
          ? () => getDisabledBeforeCustomHours(lastShift.clockInDate)
          : () => [],
      disabledMinutes:
        lastShift?.clockInDate != null
          ? (hour) => getDisabledBeforeCustomMinutes(lastShift.clockInDate, hour)
          : () => [],
    };
  };

  //region RETURN
  return (
    <>
      {!!clockOutVisible ? (
        <Modal
          width={760}
          className={`shiftc-modal ${isDarkMode ? "dark" : ""}`}
          title={
            <div
              className="shiftc-headband__row"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <div className="shiftc-headband__row">
                <span>
                  {isForgotShift
                    ? "Turni i harruar pa mbyllur"
                    : "Përfundo Turnin"}
                </span>
                <Tag color={isForgotShift ? "error" : "processing"}>
                  {isForgotShift ? "Konfirmim mbylljeje" : "Konfirmim turni"}
                </Tag>
              </div>
              <div className="shiftc-mode-toggle">
                <span className="shiftc-mode-toggle__label">Modo:</span>
                <Segmented
                  size="small"
                  value={pickerMode}
                  onChange={(v) => setPickerMode(v)}
                  options={[
                    { label: "Klasik", value: "classic" },
                    { label: "Orë", value: "clock" },
                  ]}
                />
              </div>
            </div>
          }
          open={clockOutVisible}
          onCancel={() => setClockOutVisible(false)}
          centered={true}
          maskClosable={false}
          destroyOnHidden
          footer={
            <>
              <div className="shiftc-footer-left">
                <Button onClick={() => setClockOutVisible(false)}>
                  Mbyll faqen
                </Button>
              </div>
              <div className="shiftc-footer-right">
                <Button
                  type="primary"
                  loading={saving}
                  disabled={saving}
                  icon={<LogoutOutlined />}
                  onClick={onClockOutSend}
                  style={
                    isForgotShift
                      ? { background: "#ea3943", borderColor: "#ea3943" }
                      : { background: "#1da193", borderColor: "#1da193" }
                  }
                >
                  {isForgotShift ? "Mbyll turnin e harruar" : "Përfundo Turnin"}
                </Button>
              </div>
            </>
          }
        >
          {!!lastShift?.clockInDate && (
            <>
              <div className="shiftc-headband">
                <div className="shiftc-headband__row">
                  <span className="shiftc-headband__name">{userFullName}</span>
                  {lastShift?.dayType ? (
                    <Tag color="default">{lastShift.dayType}</Tag>
                  ) : null}
                  <Tag color="default">
                    {dayjsTZ(lastShift?.clockInDate).format("DD/MM/YYYY")}
                  </Tag>
                </div>
                <div className="shiftc-headband__subtitle">
                  {isForgotShift ? (
                    <>
                      {overnightBanner?.summary ||
                        `Filloi ${dayjsTZ(lastShift?.clockInDate).format(
                          "DD/MM/YYYY HH:mm"
                        )}`}
                      . Zgjidhni llojin e mbylljes, orën e largimit dhe arsyen.
                    </>
                  ) : (
                    <>
                      Ju jeni duke përfunduar punën — kontrolloni të dhënat
                      para se ta konfirmoni.
                      {plannedShift ? (
                        <>
                          {" "}
                          Turni i përcaktuar:{" "}
                          <strong>{plannedShift.label}</strong>.
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              </div>

              {isForgotShift ? (
                <div className="shiftc-forgot-kind">
                  <div className="shiftc-card__label">Lloji i mbylljes</div>
                  <Segmented
                    block
                    value={
                      forgotKind === null
                        ? undefined
                        : forgotKind
                          ? "after"
                          : "same"
                    }
                    onChange={(v) => setForgotKind(v === "after")}
                    options={[
                      {
                        label: "Të njëjtën ditë",
                        value: "same",
                      },
                      {
                        label: "Pas mesnatës",
                        value: "after",
                      },
                    ]}
                  />
                  {forgotKind === false ? (
                    <div className="shiftc-card__sub" style={{ marginTop: 6 }}>
                      Ora e mbarimit bie në{" "}
                      {dayjsTZ(lastShift?.clockInDate).format("DD/MM/YYYY")} —
                      jo ora e tanishme.
                    </div>
                  ) : null}
                </div>
              ) : null}

              {shiftWindowSuspicious ? (
                <Alert
                  className="shiftc-alert"
                  type="warning"
                  showIcon
                  message="Ora e mbarimit është jashtë intervalit të lejuar."
                  description={`Turni i përcaktuar është ${plannedShift?.label}. Kontrolloni orën para se ta konfirmoni.`}
                />
              ) : null}

              {!isForgotShift && (gracePeriod > 0 || graceBypass) ? (
                <Alert
                  className="shiftc-alert"
                  type="info"
                  showIcon
                  message={graceInfoMessage("out", gracePeriod, graceBypass)}
                />
              ) : null}

              {graceWarnings.enable ? (
                <Alert
                  className="shiftc-alert"
                  type="warning"
                  showIcon
                  message={graceWarnings.text}
                />
              ) : null}

              <div className="shiftc-elapsed">
                <div>
                  <div className="shiftc-elapsed__label">
                    Koha e kaluar në punë
                  </div>
                  <div className="shiftc-elapsed__value">
                    {formatHm(elapsedMinutes)}
                  </div>
                  <div className="shiftc-elapsed__hint">
                    Që nga {dayjsTZ(lastShift?.clockInDate).format("HH:mm")}
                    {plannedShift
                      ? ` · Plani: ${formatHm(plannedShift.minutes)}`
                      : ""}
                  </div>
                </div>
                <span className="shiftc-elapsed__pill">
                  <HourglassOutlined />
                  {plannedShift
                    ? `${Math.round(progressPct)}% e turnit`
                    : "Në vazhdim"}
                </span>
                <div className="shiftc-elapsed__progress">
                  <div className="shiftc-elapsed__progress-track">
                    <div
                      className="shiftc-elapsed__progress-fill"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="shiftc-cards">
                <div className="shiftc-card shiftc-card--muted">
                  <div className="shiftc-card__label">
                    <LoginOutlined /> Fillimi
                  </div>
                  <div className="shiftc-card__value">
                    {dayjsTZ(lastShift?.clockInDate).format("HH:mm")}
                  </div>
                  <div className="shiftc-card__sub">
                    {dayjsTZ(lastShift?.clockInDate).format("DD/MM/YYYY")}
                  </div>
                </div>

                <div className="shiftc-card shiftc-card--active">
                  <div className="shiftc-card__label">
                    <span className="shiftc-required-asterisk">*</span>
                    <LogoutOutlined /> Mbarimi
                    {isForgotShift && forgotKind === false ? (
                      <span className="shiftc-card__sub">
                        {" "}
                        · {dayjsTZ(lastShift?.clockInDate).format("DD/MM/YYYY")}
                      </span>
                    ) : null}
                  </div>
                  <div className="shiftc-card__pickerRow">
                    {pickerMode === "clock" ? (
                      <ClockFaceTimePicker
                        value={previewClockOut}
                        onChange={onChange}
                        isDarkMode={isDarkMode}
                        disabledHours={clockOutDisabledHours}
                        placeholder="HH:MM"
                        disabled={isForgotShift && forgotKind === null}
                      />
                    ) : (
                      <TimePicker
                        className="shiftc-timepicker"
                        value={previewClockOut}
                        placeholder="HH:MM"
                        classNames={{
                          popup: {
                            root: `shiftc-timepicker-dropdown ${
                              isDarkMode ? "darkDropdown" : ""
                            }`,
                          },
                        }}
                        format="HH:mm"
                        disabledTime={classicDisabledTime}
                        showNow={false}
                        allowClear={false}
                        disabled={isForgotShift && forgotKind === null}
                        onChange={onChange}
                      />
                    )}
                  </div>
                  {!(isForgotShift && forgotKind === false) ? (
                    <div className="shiftc-presets">
                      <Button
                        className="shiftc-preset-chip shiftc-preset-chip--primary"
                        icon={<ClockCircleOutlined />}
                        disabled={isForgotShift && forgotKind === null}
                        onClick={() => {
                          if (isForgotShift) {
                            if (forgotKind !== true) return;
                            setNewSh((prev) => ({
                              ...prev,
                              clockOutDate: dayjsTZ().second(0).millisecond(0),
                            }));
                            setGraceWarning({
                              enable: true,
                              text: "Shënimi me arsye është i detyrueshëm për turnin e harruar.",
                            });
                            return;
                          }
                          setNewSh((prev) => ({
                            ...prev,
                            clockOutDate: getAutoSuggest(dayjsTZ()),
                          }));
                          setGraceWarning({ enable: false, text: "" });
                        }}
                      >
                        Tani
                        <span className="shiftc-preset-chip__time">
                          {dayjsTZ().format("HH:mm")}
                        </span>
                      </Button>
                    </div>
                  ) : null}
                  <div className="shiftc-card__sub">
                    {isForgotShift && forgotKind === false
                      ? `Zgjidhni HH:MM për ${dayjsTZ(
                          lastShift?.clockInDate
                        ).format("DD/MM/YYYY")}.`
                      : isForgotShift
                        ? "Zgjidhni llojin, pastaj orën e largimit."
                        : "Mund të shkruani direkt HH:MM ose përdorni ‘Tani’."}
                  </div>
                </div>

                <div className="shiftc-card shiftc-card--muted">
                  <div className="shiftc-card__label">
                    <ClockCircleOutlined /> Totali
                  </div>
                  <div className="shiftc-card__value">
                    {formatHm(elapsedMinutes)}
                  </div>
                  <div className="shiftc-card__sub">
                    {plannedShift
                      ? `Plani ${formatHm(plannedShift.minutes)}`
                      : "Llogaritur automatikisht"}
                  </div>
                </div>
              </div>

              <div
                className={`shiftc-notes ${
                  noteRequired && noteCharCount < MIN_NOTE_CHARS
                    ? "shiftc-required-glow"
                    : ""
                }`}
              >
                <div className="shiftc-notes__head">
                  <span className="shiftc-notes__title">
                    {noteRequired ? (
                      <span className="shiftc-required-asterisk">*</span>
                    ) : null}
                    Shënimi i përfundimit
                  </span>
                  {noteRequired ? (
                    <span className="shiftc-notes__required">
                      I detyrueshëm (min. {MIN_NOTE_CHARS} karaktere) ·{" "}
                      {noteCharCount}/{MIN_NOTE_CHARS}
                    </span>
                  ) : (
                    <span className="shiftc-card__sub">
                      Opsional · {noteCharCount} karaktere
                    </span>
                  )}
                </div>
                <RichTextEditor
                  className={"to-note-textarea"}
                  value={newShift.note}
                  onChange={(e) => setNewSh((prev) => ({ ...prev, note: e }))}
                />
              </div>

              <div className="shiftc-summary">
                <span>
                  Statusi: <strong>Për konfirmim</strong>
                </span>
                {deltaMinutes !== null ? (
                  <span>
                    Diferenca:{" "}
                    <strong
                      className={
                        deltaMinutes >= 0
                          ? "shiftc-summary__delta-pos"
                          : "shiftc-summary__delta-neg"
                      }
                    >
                      {deltaMinutes >= 0 ? "+" : "−"}
                      {Math.abs(deltaMinutes)} min
                    </strong>
                  </span>
                ) : null}
                <span>
                  Totali: <strong>{formatHm(elapsedMinutes)}</strong>
                </span>
                {/* Original "Lloji i turnit" Select — preserved as-is
                    from the pre-redesign modal: disabled, programFields-
                    driven, dark-mode dropdown class, filtered by
                    fieldName "ClockIn Types". Do not change the
                    population logic. Rendered inline & compact so it
                    doesn't eat a whole card row. */}
                <span className="shiftc-summary__type">
                  <span className="shiftc-required-asterisk">*</span>
                  Lloji:&nbsp;
                  <Select
                    className="daytype-select shiftc-shifttype-select shiftc-shifttype-select--inline"
                    classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
                    disabled
                    showSearch
                    size="small"
                    value={lastShift?.dayType}
                    placeholder="—"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {programFields
                      ?.find((el) => el.fieldName === "ClockIn Types")
                      ?.fieldOptions?.map((el, idx) => (
                        <Option key={`shiftType-${idx}`} value={el?.name}>
                          {el?.description}
                        </Option>
                      ))}
                  </Select>
                </span>
              </div>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}

export default ClockOutModal;
